import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './styles/globals.css'
import App from './App.tsx'
import { DevTools } from './components/DevTools'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 60s stale time covers most "I just navigated to this page" reuses
      // without serving truly old data. Per-query overrides via useQuery
      // for endpoints that need stricter freshness.
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        // Don't retry auth failures — token's gone, retrying won't help
        const status = (error as { status?: number } | null)?.status
        if (status === 401 || status === 403) return false
        // Single retry only — failed requests now surface in ~1s instead
        // of the default exponential 1+2+4s chain that froze the UI.
        return failureCount < 1
      },
      retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 2000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Mutations never retry — a failed POST/PUT should error
      // immediately so the user can decide what to do.
      retry: 0,
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      {import.meta.env.DEV && <DevTools />}
    </QueryClientProvider>
  </StrictMode>,
)
